/**
 * Import des exports qu'aucun contenu ne trahit : Universal Password Manager,
 * Password Agent, PasswordWallet, SplashID Safe et Ascendo DataVault ecrivent
 * un CSV **sans ligne d'en-tete**, et BlackBerry Password Keeper reprend mot
 * pour mot l'en-tete de l'ancien CSV LastPass (sa colonne `grouping` y porte le
 * TYPE de l'element, pas un dossier). Deviner serait lire n'importe quel CSV de
 * travers : l'utilisateur choisit le format a l'ecran d'import, et seul ce choix
 * aiguille ici (`parseImportFile`, option `format`).
 *
 * Lus comme Bitwarden les lit (`libs/importer/src/importers/*` de
 * `bitwarden/clients` @ `fc5de8618106`), separateur devine comme papaparse
 * (`,`, tabulation, `;` ou `|`), et au-dela : une ligne qui n'a pas la forme du
 * format choisi est REFUSEE avec son numero (Bitwarden la saute en silence --
 * un mauvais format choisi importait un coffre ampute sans le dire) ; un dossier
 * Password Agent `A > B` donne deux niveaux (Bitwarden n'en garde qu'un, son
 * decoupage `\` etant ecrase) ; un champ Ascendo hors identifiant / mot de passe
 * / adresse devient un champ personnalise, masque sous un libelle de secret
 * (PIN, CVV). Le mot de passe n'est jamais rogne.
 *
 * Purement local, aucune E/S reseau : texte -> `ImportedEntry`.
 */
import type { EntryCustomField } from '@klavox/crypto-core';
import { csvFreeColumnField, folderPathOf, joinNotes, parseCsvLine, splitCsvRecords, type ImportedEntry } from './csv-import.js';
import { serverHost } from './typed-fields.js';

/** Formats proposes a l'ecran d'import ; `auto` = reconnaissance au contenu (`parseImportFile`). */
export const IMPORT_FORMATS = [
  {
    id: 'auto',
    label: 'Reconnaître automatiquement',
    hint: 'Klavox reconnaît l’export à son contenu. Choisissez un format seulement pour un export sans ligne d’en-tête.',
  },
  {
    id: 'upm',
    label: 'Universal Password Manager (CSV)',
    hint: 'Dans Universal Password Manager : Database → Export, puis enregistrez le fichier CSV.',
  },
  {
    id: 'passwordagent',
    label: 'Password Agent (CSV)',
    hint: 'Dans Password Agent : File → Export, « Fields to export » en cochant tous les champs, Output format CSV, puis Start.',
  },
  {
    id: 'passwordwallet',
    label: 'PasswordWallet (TXT)',
    hint: 'Dans PasswordWallet : File → Export → Visible entries to text file, puis enregistrez le fichier TXT.',
  },
  {
    id: 'splashid',
    label: 'SplashID Safe (CSV)',
    hint: 'Dans SplashID Safe : cadenas bleu en haut à droite → Export → Export as CSV.',
  },
  {
    id: 'ascendo',
    label: 'Ascendo DataVault (CSV)',
    hint: 'Dans Ascendo DataVault : Tools → Export, option « All Items (DVX, CSV) », puis Ok.',
  },
  {
    id: 'blackberry',
    label: 'BlackBerry Password Keeper (CSV)',
    hint: 'Dans BlackBerry Password Keeper : Settings → Import/Export → Export Passwords, fichier CSV non chiffré.',
  },
] as const;

export type ImportFormat = (typeof IMPORT_FORMATS)[number]['id'];

/** Format choisi explicitement, jamais devine. */
export type ExplicitImportFormat = Exclude<ImportFormat, 'auto'>;

export function isImportFormat(value: string): value is ImportFormat {
  return IMPORT_FORMATS.some((format) => format.id === value);
}

/** Nom d'un format, pour les messages d'erreur. */
function formatName(format: ExplicitImportFormat): string {
  return IMPORT_FORMATS.find((candidate) => candidate.id === format)!.label.replace(/ \((CSV|TXT)\)$/u, '');
}

/** Separateurs essayes, dans l'ordre de preference a egalite (papaparse : `,` `\t` `|` `;`). */
const DELIMITERS = [',', '\t', ';', '|'] as const;

/**
 * Separateur d'un export sans en-tete : celui qui donne a la plupart des
 * lignes un meme nombre de colonnes (au moins deux) ; a egalite, le plus large
 * decoupage. Une virgule dans un mot de passe d'un export a tabulations ne fait
 * donc pas basculer le decoupage.
 */
function guessDelimiter(records: readonly string[]): string {
  let best: string = DELIMITERS[0];
  let bestRows = 0;
  let bestWidth = 0;
  for (const delimiter of DELIMITERS) {
    const counts = new Map<number, number>();
    for (const record of records.slice(0, 20)) {
      const width = parseCsvLine(record, delimiter).length;
      if (width > 1) counts.set(width, (counts.get(width) ?? 0) + 1);
    }
    for (const [width, rows] of counts) {
      if (rows > bestRows || (rows === bestRows && width > bestWidth)) {
        best = delimiter;
        bestRows = rows;
        bestWidth = width;
      }
    }
  }
  return best;
}

/** Lignes d'un export sans en-tete, decoupees ; refuse un fichier vide. */
function readRows(text: string, format: ExplicitImportFormat): string[][] {
  const records = splitCsvRecords(text);
  if (records.length === 0) throw new Error(`fichier ${formatName(format)} vide`);
  const delimiter = guessDelimiter(records);
  return records.map((record) => parseCsvLine(record, delimiter));
}

/** Une ligne qui n'a pas la forme du format choisi : refusee, jamais sautee en silence. */
function wrongShape(format: ExplicitImportFormat, line: number, width: number, expected: string): Error {
  return new Error(
    `ligne ${line} : ${width} colonne(s), un export ${formatName(format)} en porte ${expected} -- le format choisi est-il le bon ?`,
  );
}

interface LoginParts {
  readonly name: string;
  readonly username: string;
  /** Brut : un mot de passe n'est jamais rogne, ses espaces font partie du secret. */
  readonly password: string;
  readonly url: string;
  readonly notes: string;
  readonly folderPath?: readonly string[];
  readonly customFields?: readonly EntryCustomField[];
  readonly favorite?: boolean;
  /** L'export dit que l'element est une note (BlackBerry `grouping` = `note`). */
  readonly note?: boolean;
}

/**
 * Entree importee ; une ligne sans identifiant, mot de passe ni adresse devient
 * une note (`convertToNoteIfNeeded` de Bitwarden), une ligne entierement vide
 * n'est pas une entree.
 */
function toEntry(parts: LoginParts): ImportedEntry | null {
  const username = parts.note === true ? '' : parts.username.trim();
  const password = parts.note === true ? '' : parts.password;
  const url = parts.note === true ? '' : parts.url.trim();
  const name = parts.name.trim();
  const notes = parts.notes.trim();
  const customFields = parts.customFields ?? [];
  const isLogin = username.length > 0 || password.length > 0 || url.length > 0;
  if (!isLogin && name.length === 0 && notes.length === 0 && customFields.length === 0) return null;
  const host = url.length > 0 ? serverHost(url).replace(/^www\./u, '') : '';
  return {
    type: isLogin ? 'login' : 'note',
    name: name || host || url || username || 'Sans nom',
    username,
    password,
    uris: url.length > 0 ? [url] : [],
    notes,
    totpSecret: '',
    favorite: parts.favorite === true,
    ...(customFields.length > 0 ? { customFields } : {}),
    ...(parts.folderPath !== undefined && parts.folderPath.length > 0 ? { folderPath: parts.folderPath } : {}),
  };
}

/** Universal Password Manager : `compte, identifiant, mot de passe, adresse, notes`, cinq colonnes exactement. */
function parseUpm(rows: readonly string[][]): (ImportedEntry | null)[] {
  return rows.map((cells, index) => {
    if (cells.length !== 5) throw wrongShape('upm', index + 1, cells.length, '5');
    const [name, username, password, url, notes] = cells as [string, string, string, string, string];
    return toEntry({ name, username, password, url, notes });
  });
}

/** Dossier Password Agent : `(None)` = racine ; niveaux separes par `\` ou `>` (`Banques > Pro`). */
function passwordAgentFolder(raw: string): readonly string[] {
  if (raw.trim() === '(None)') return [];
  return raw
    .split(/[\\>]/u)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/**
 * Password Agent : ancienne version a 5 colonnes (`nom, identifiant, mot de
 * passe, adresse, notes`) ; version actuelle a 9 colonnes et plus (`nom,
 * identifiant, mot de passe, notes, adresse, ..., groupe` en 9e), variante a
 * 10 colonnes ouverte par `0` (colonnes de Bitwarden reprises telles quelles).
 */
function parsePasswordAgent(rows: readonly string[][]): (ImportedEntry | null)[] {
  return rows.map((cells, index) => {
    const at = (column: number): string => cells[column] ?? '';
    if (cells.length === 5) {
      return toEntry({ name: at(0), username: at(1), password: at(2), url: at(3), notes: at(4) });
    }
    if (cells.length < 9) throw wrongShape('passwordagent', index + 1, cells.length, '5, ou 9 et plus');
    const shifted = cells.length === 10 && at(0) === '0';
    return toEntry({
      name: at(shifted ? 1 : 0),
      username: at(shifted ? 2 : 1),
      password: at(shifted ? 3 : 2),
      url: at(4),
      notes: at(shifted ? 5 : 3),
      folderPath: passwordAgentFolder(at(shifted ? 9 : 8)),
    });
  });
}

/** PasswordWallet : `nom, adresse, identifiant, mot de passe, notes (¬ = saut de ligne), dossier`, colonnes finales facultatives. */
function parsePasswordWallet(rows: readonly string[][]): (ImportedEntry | null)[] {
  return rows.map((cells) => {
    const at = (column: number): string => cells[column] ?? '';
    return toEntry({
      name: at(0),
      url: at(1),
      username: at(2),
      password: at(3),
      notes: at(4).split('¬').join('\n'),
      ...folderPathOf(at(5)),
    });
  });
}

/** Types SplashID dont les colonnes 3 a 5 sont identifiant, mot de passe, adresse. */
const SPLASHID_LOGIN_TYPES = new Set(['Web Logins', 'Servers', 'Email Accounts']);

/**
 * SplashID Safe : `type, nom, champs..., date, notes, categorie`. Pour un
 * identifiant (site, serveur, messagerie), les trois premiers champs sont
 * identifiant, mot de passe, adresse ; les autres champs (carte, banque...)
 * vont en notes, une valeur par ligne, comme chez Bitwarden.
 */
function parseSplashId(rows: readonly string[][]): (ImportedEntry | null)[] {
  return rows.map((cells, index) => {
    if (cells.length < 3) throw wrongShape('splashid', index + 1, cells.length, 'au moins 3');
    const at = (column: number): string => cells[column] ?? '';
    const isLogin = SPLASHID_LOGIN_TYPES.has(at(0).trim());
    const extra = cells.slice(isLogin ? 5 : 2, Math.max(isLogin ? 5 : 2, cells.length - 3)).map((cell) => cell.trim());
    return toEntry({
      name: at(1),
      username: isLogin ? at(2) : '',
      password: isLogin ? at(3) : '',
      url: isLogin ? at(4) : '',
      notes: joinNotes([at(cells.length - 2), ...extra]),
      ...folderPathOf(at(cells.length - 1)),
    });
  });
}

/** Libelles Ascendo reconnus comme mot de passe, identifiant, adresse (`passwordFieldNames`... de Bitwarden). */
const ASCENDO_PASSWORD = new Set(['password', 'pass word', 'passphrase', 'pass phrase', 'pass', 'code', 'code word', 'codeword', 'secret', 'secret word', 'personpwd', 'key', 'keyword', 'key word', 'keyphrase', 'key phrase', 'form_pw', 'wppassword', 'pin', 'pwd', 'pw', 'pword', 'passwd', 'p', 'serial', 'serial#', 'license key', 'reg #', 'passwort', 'mot de passe']);
const ASCENDO_USERNAME = new Set(['user', 'name', 'user name', 'username', 'login name', 'email', 'e-mail', 'id', 'userid', 'user id', 'login', 'form_loginname', 'wpname', 'mail', 'loginid', 'login id', 'log', 'personlogin', 'first name', 'last name', 'card#', 'account #', 'member', 'member #', 'nom', 'benutzername', 'identifiant']);
const ASCENDO_URI = new Set(['url', 'hyper link', 'hyperlink', 'link', 'host', 'hostname', 'host name', 'server', 'address', 'hyper ref', 'href', 'web', 'website', 'web site', 'site', 'web-site', 'uri', 'ort', 'adresse']);

/**
 * Ascendo DataVault : `nom, (libelle, valeur)..., notes`. Le premier libelle de
 * mot de passe, d'identifiant et d'adresse donne le role ; les autres paires
 * deviennent des champs personnalises (masques sous un libelle de secret).
 */
function parseAscendo(rows: readonly string[][]): (ImportedEntry | null)[] {
  return rows.map((cells, index) => {
    if (cells.length < 2 || (cells.length > 2 && cells.length % 2 !== 0)) {
      throw wrongShape('ascendo', index + 1, cells.length, 'un nombre pair (nom, paires libelle / valeur, notes)');
    }
    let username = '';
    let password = '';
    let url = '';
    const customFields: EntryCustomField[] = [];
    for (let i = 1; i < cells.length - 1; i += 2) {
      const label = cells[i]!.trim();
      const value = cells[i + 1]!;
      if (value.trim().length === 0) continue;
      const role = label.toLowerCase();
      if (password.length === 0 && ASCENDO_PASSWORD.has(role)) password = value;
      else if (username.length === 0 && ASCENDO_USERNAME.has(role)) username = value;
      else if (url.length === 0 && ASCENDO_URI.has(role)) url = value;
      else customFields.push(csvFreeColumnField(label, value)!);
    }
    return toEntry({ name: cells[0]!, username, password, url, notes: cells[cells.length - 1]!, customFields });
  });
}

/** Colonnes de l'export BlackBerry Password Keeper (en-tete de l'ancien CSV LastPass). */
const BLACKBERRY_COLUMNS = ['url', 'username', 'password', 'extra', 'name', 'grouping', 'fav'] as const;

/**
 * BlackBerry Password Keeper : en-tete `url,username,password,extra,name,
 * grouping,fav` ; `grouping` porte le type (`note`, `list` -- une liste n'est
 * pas un secret et n'est pas reprise), jamais un dossier.
 */
function parseBlackBerry(text: string): (ImportedEntry | null)[] {
  const records = splitCsvRecords(text);
  if (records.length === 0) throw new Error(`fichier ${formatName('blackberry')} vide`);
  const header = parseCsvLine(records[0]!).map((label) => label.trim().toLowerCase());
  const missing = BLACKBERRY_COLUMNS.filter((column) => !header.includes(column));
  if (missing.length > 0) {
    throw new Error(`en-tete BlackBerry Password Keeper attendu (${BLACKBERRY_COLUMNS.join(', ')}) : ${missing.join(', ')} absent(s)`);
  }
  return records.slice(1).map((record) => {
    const cells = parseCsvLine(record);
    const at = (column: (typeof BLACKBERRY_COLUMNS)[number]): string => cells[header.indexOf(column)] ?? '';
    const kind = at('grouping').trim().toLowerCase();
    if (kind === 'list') return null;
    return toEntry({
      name: at('name'),
      username: at('username'),
      password: at('password'),
      url: at('url'),
      notes: at('extra'),
      favorite: at('fav').trim() === '1',
      note: kind === 'note',
    });
  });
}

/**
 * Lit un export dans le format CHOISI par l'utilisateur.
 *
 * @throws {Error} fichier vide, ou ligne qui n'a pas la forme du format (numero de ligne donne)
 */
export function parseExplicitFormat(format: ExplicitImportFormat, text: string): ImportedEntry[] {
  const parsed = (() => {
    switch (format) {
      case 'upm':
        return parseUpm(readRows(text, format));
      case 'passwordagent':
        return parsePasswordAgent(readRows(text, format));
      case 'passwordwallet':
        return parsePasswordWallet(readRows(text, format));
      case 'splashid':
        return parseSplashId(readRows(text, format));
      case 'ascendo':
        return parseAscendo(readRows(text, format));
      case 'blackberry':
        return parseBlackBerry(text);
    }
  })();
  return parsed.filter((entry): entry is ImportedEntry => entry !== null);
}
